import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  ArrowLeft, Plus, Save, Copy, Trash2, Edit2, X, Loader2,
  FileText, ChevronRight, AlertCircle, ListChecks, CheckCircle2
} from "lucide-react";

const DAYS = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];
const SHIFTS = ["MAÑANA", "TARDE", "NOCHE"];

const emptyEntry = () => ({
  shift: "MAÑANA",
  service_type: "",
  vehicle_plate: "",
  personnel_name: "",
  category: "",
  sector_route: "",
});

function TemplateRow({ template, isActive, onSelect, onDuplicate, onRename, onDelete }) {
  const entryCount = Object.values(template.days || {}).reduce((s, d) => s + (d?.length || 0), 0);
  return (
    <button
      onClick={onSelect}
      className={`w-full text-left p-3 rounded-xl border transition-all ${
        isActive ? "border-[#007934] bg-[#007934]/5 shadow-sm" : "border-slate-200 bg-white hover:border-slate-300"
      }`}
      data-testid={`tpl-row-${template.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-sm text-slate-800 truncate">{template.name}</p>
          <p className="text-xs text-slate-500 mt-0.5">
            {entryCount} entradas · creada {new Date(template.created_at).toLocaleDateString("es-ES")}
          </p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
          <button onClick={onRename} className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded" title="Renombrar">
            <Edit2 size={13} />
          </button>
          <button onClick={onDuplicate} className="p-1.5 text-slate-400 hover:text-violet-700 hover:bg-violet-50 rounded" title="Duplicar">
            <Copy size={13} />
          </button>
          <button onClick={onDelete} className="p-1.5 text-slate-400 hover:text-rose-700 hover:bg-rose-50 rounded" title="Eliminar">
            <Trash2 size={13} />
          </button>
        </div>
      </div>
    </button>
  );
}

export default function PlanTemplatesEditor() {
  const navigate = useNavigate();
  const { authAxios } = useAuth();
  const [searchParams] = useSearchParams();
  const initialContractId = searchParams.get("contract_id") || "";

  const [contracts, setContracts] = useState([]);
  const [contractId, setContractId] = useState(initialContractId);
  const [templates, setTemplates] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [active, setActive] = useState(null);  // full template
  const [activeDay, setActiveDay] = useState("Lunes");
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [createName, setCreateName] = useState("");
  const [showCreate, setShowCreate] = useState(false);

  // Catalog for current contract (services + plates + personnel) for dropdowns
  const [catalog, setCatalog] = useState({ services: [], plates: [], personnel: [], categories: [] });

  useEffect(() => {
    authAxios.get("/contracts").then((r) => {
      setContracts(r.data);
      if (!contractId && r.data.length > 0) setContractId(r.data[0].id);
    });
  }, [authAxios, contractId]);

  // Load contract catalog
  useEffect(() => {
    if (!contractId) return;
    authAxios.get(`/contracts/${contractId}/catalog`).then((r) => {
      setCatalog({
        services: r.data.services || [],
        categories: r.data.categories || [],
        plates: (r.data.vehicles || []).map((v) => v.plate).filter(Boolean),
        personnel: (r.data.personnel || []).map((p) => p.full_name).filter(Boolean),
      });
    }).catch(() => setCatalog({ services: [], plates: [], personnel: [], categories: [] }));
  }, [authAxios, contractId]);

  const fetchTemplates = useCallback(async () => {
    if (!contractId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await authAxios.get(`/plans/templates?contract_id=${contractId}`);
      setTemplates(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "No se pudieron cargar las plantillas");
    } finally {
      setLoading(false);
    }
  }, [authAxios, contractId]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const loadTemplate = async (id) => {
    if (dirty && !window.confirm("Tienes cambios sin guardar. ¿Descartar?")) return;
    setActiveId(id);
    setDirty(false);
    try {
      const r = await authAxios.get(`/plans/templates/${id}`);
      setActive(r.data);
      setActiveDay("Lunes");
    } catch (e) {
      setError(e.response?.data?.detail || "No se pudo cargar la plantilla");
    }
  };

  const createTemplate = async () => {
    const name = createName.trim();
    if (!name) return;
    try {
      const r = await authAxios.post("/plans/templates", { contract_id: contractId, name });
      setShowCreate(false);
      setCreateName("");
      await fetchTemplates();
      await loadTemplate(r.data.id);
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(e.response?.data?.detail || "No se pudo crear");
    }
  };

  const renameTemplate = async (template) => {
    // eslint-disable-next-line no-alert
    const newName = window.prompt("Nuevo nombre:", template.name);
    if (!newName || newName === template.name) return;
    await authAxios.put(`/plans/templates/${template.id}`, { name: newName });
    await fetchTemplates();
    if (active?.id === template.id) setActive({ ...active, name: newName });
  };

  const duplicateTemplate = async (template) => {
    // eslint-disable-next-line no-alert
    const newName = window.prompt("Nombre de la copia:", `${template.name} (copia)`);
    if (!newName) return;
    await authAxios.post(`/plans/templates/${template.id}/duplicate`, { name: newName });
    await fetchTemplates();
  };

  const deleteTemplate = async (template) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`¿Eliminar la plantilla "${template.name}"? No se puede deshacer.`)) return;
    await authAxios.delete(`/plans/templates/${template.id}`);
    if (active?.id === template.id) {
      setActive(null);
      setActiveId(null);
    }
    await fetchTemplates();
  };

  // ─── Day editor helpers ───────────────────────────────────────────────────
  const dayEntries = active?.days?.[activeDay] || [];
  const updateEntries = (newEntries) => {
    setActive({ ...active, days: { ...active.days, [activeDay]: newEntries } });
    setDirty(true);
  };
  const addEntry = () => updateEntries([...dayEntries, emptyEntry()]);
  const updateEntry = (idx, patch) => {
    const copy = [...dayEntries];
    copy[idx] = { ...copy[idx], ...patch };
    updateEntries(copy);
  };
  const removeEntry = (idx) => updateEntries(dayEntries.filter((_, i) => i !== idx));

  const saveActive = async () => {
    if (!active) return;
    setSaving(true);
    try {
      await authAxios.put(`/plans/templates/${active.id}`, { name: active.name, days: active.days });
      setDirty(false);
      await fetchTemplates();
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(e.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const totalEntries = useMemo(
    () => active ? Object.values(active.days || {}).reduce((s, d) => s + d.length, 0) : 0,
    [active]
  );

  return (
    <div className="space-y-4" data-testid="plan-templates-editor">
      {/* Top bar */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/planning")}
            className="p-2 hover:bg-slate-100 rounded-lg text-slate-500"
            data-testid="back-to-planning"
          >
            <ArrowLeft size={16} />
          </button>
          <div>
            <h1 className="text-xl font-black text-slate-900">Plantillas de Planificación</h1>
            <p className="text-xs text-slate-500">Biblioteca de plantillas semanales reutilizables del contrato.</p>
          </div>
        </div>
        <select
          value={contractId}
          onChange={(e) => { setContractId(e.target.value); setActive(null); setActiveId(null); }}
          className="form-input text-sm"
          data-testid="tpl-contract-select"
        >
          {contracts.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Sidebar: list */}
        <div className="card-white p-4 lg:col-span-1">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold text-slate-800 text-sm flex items-center gap-2">
              <ListChecks size={14} /> Plantillas ({templates.length})
            </h2>
            <button
              onClick={() => setShowCreate(true)}
              className="text-xs font-semibold bg-[#007934] text-white px-3 py-1.5 rounded-lg flex items-center gap-1 hover:bg-[#005c27]"
              data-testid="tpl-create-btn"
            >
              <Plus size={12} /> Nueva
            </button>
          </div>

          {showCreate && (
            <div className="mb-3 p-3 bg-slate-50 rounded-lg space-y-2">
              <input
                autoFocus
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && createTemplate()}
                placeholder="Nombre de la nueva plantilla…"
                className="form-input text-sm w-full"
                data-testid="tpl-create-input"
              />
              <div className="flex gap-2">
                <button onClick={createTemplate} className="flex-1 text-xs font-semibold bg-[#007934] text-white py-1.5 rounded-lg" data-testid="tpl-create-confirm">
                  Crear
                </button>
                <button onClick={() => { setShowCreate(false); setCreateName(""); }} className="text-xs font-semibold text-slate-500 px-3 py-1.5 hover:bg-slate-100 rounded-lg">
                  Cancelar
                </button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="p-8 text-center text-slate-400">
              <Loader2 className="animate-spin mx-auto mb-2" size={20} />
            </div>
          ) : templates.length === 0 ? (
            <div className="p-8 text-center text-slate-400">
              <FileText className="mx-auto mb-2" size={28} />
              <p className="text-sm">Sin plantillas todavía</p>
              <p className="text-xs mt-1">Crea una desde el botón + Nueva</p>
            </div>
          ) : (
            <div className="space-y-2">
              {templates.map((t) => (
                <TemplateRow
                  key={t.id}
                  template={t}
                  isActive={activeId === t.id}
                  onSelect={() => loadTemplate(t.id)}
                  onRename={() => renameTemplate(t)}
                  onDuplicate={() => duplicateTemplate(t)}
                  onDelete={() => deleteTemplate(t)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Main: editor */}
        <div className="lg:col-span-2">
          {error && (
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-sm text-rose-700 mb-3 flex items-center gap-2">
              <AlertCircle size={14} /> {error}
            </div>
          )}

          {!active ? (
            <div className="card-white p-12 text-center text-slate-400">
              <ChevronRight className="mx-auto mb-3" size={32} />
              <p className="font-semibold">Selecciona una plantilla</p>
              <p className="text-xs mt-1">Elige una de la columna izquierda para editar sus 7 días.</p>
            </div>
          ) : (
            <div className="card-white p-5">
              {/* Header */}
              <div className="flex items-center justify-between pb-4 mb-4 border-b border-slate-100">
                <div>
                  <h2 className="font-bold text-slate-900">{active.name}</h2>
                  <p className="text-xs text-slate-500">{totalEntries} entradas en toda la semana</p>
                </div>
                <div className="flex items-center gap-2">
                  {dirty && (
                    <span className="text-xs font-semibold text-amber-700 bg-amber-50 px-2 py-1 rounded-full">
                      Cambios sin guardar
                    </span>
                  )}
                  <button
                    onClick={saveActive}
                    disabled={!dirty || saving}
                    className="flex items-center gap-2 bg-[#007934] hover:bg-[#005c27] text-white px-4 py-2 rounded-lg text-sm font-semibold shadow-sm disabled:opacity-50"
                    data-testid="tpl-save-btn"
                  >
                    {saving ? <Loader2 size={14} className="animate-spin" /> : dirty ? <Save size={14} /> : <CheckCircle2 size={14} />}
                    {dirty ? "Guardar cambios" : "Guardado"}
                  </button>
                </div>
              </div>

              {/* Day tabs */}
              <div className="flex flex-wrap gap-1 mb-4">
                {DAYS.map((d) => {
                  const count = active.days?.[d]?.length || 0;
                  return (
                    <button
                      key={d}
                      onClick={() => setActiveDay(d)}
                      className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-all ${
                        activeDay === d
                          ? "bg-[#007934] text-white"
                          : count > 0 ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-slate-100 text-slate-500"
                      }`}
                      data-testid={`tpl-day-${d}`}
                    >
                      {d} {count > 0 && <span className="ml-1 opacity-70">({count})</span>}
                    </button>
                  );
                })}
              </div>

              {/* Entries table */}
              {dayEntries.length === 0 ? (
                <div className="p-8 text-center text-slate-400 border-2 border-dashed border-slate-200 rounded-xl">
                  <p className="text-sm">Sin entradas para {activeDay}</p>
                  <button onClick={addEntry} className="mt-3 text-xs font-semibold bg-[#007934] text-white px-3 py-1.5 rounded-lg inline-flex items-center gap-1" data-testid="tpl-add-first-entry">
                    <Plus size={12} /> Añadir primera entrada
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {dayEntries.map((e, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 p-3 bg-slate-50 rounded-lg" data-testid={`tpl-entry-${idx}`}>
                      <select
                        value={e.shift || "MAÑANA"}
                        onChange={(ev) => updateEntry(idx, { shift: ev.target.value })}
                        className="col-span-2 form-input text-xs"
                      >
                        {SHIFTS.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <input
                        list="tpl-services"
                        value={e.service_type || ""}
                        onChange={(ev) => updateEntry(idx, { service_type: ev.target.value })}
                        placeholder="Servicio"
                        className="col-span-3 form-input text-xs"
                      />
                      <input
                        list="tpl-plates"
                        value={e.vehicle_plate || ""}
                        onChange={(ev) => updateEntry(idx, { vehicle_plate: ev.target.value })}
                        placeholder="Matrícula"
                        className="col-span-2 form-input text-xs font-mono"
                      />
                      <input
                        list="tpl-personnel"
                        value={e.personnel_name || ""}
                        onChange={(ev) => updateEntry(idx, { personnel_name: ev.target.value })}
                        placeholder="Personal"
                        className="col-span-2 form-input text-xs"
                      />
                      <input
                        value={e.sector_route || ""}
                        onChange={(ev) => updateEntry(idx, { sector_route: ev.target.value })}
                        placeholder="Sector"
                        className="col-span-2 form-input text-xs"
                      />
                      <button
                        onClick={() => removeEntry(idx)}
                        className="col-span-1 text-rose-500 hover:bg-rose-50 rounded-lg flex items-center justify-center"
                        title="Eliminar"
                        data-testid={`tpl-remove-entry-${idx}`}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={addEntry}
                    className="w-full mt-2 py-2 text-xs font-semibold text-[#007934] hover:bg-[#007934]/5 rounded-lg border-2 border-dashed border-[#007934]/30 flex items-center justify-center gap-1"
                    data-testid="tpl-add-entry"
                  >
                    <Plus size={12} /> Añadir entrada
                  </button>
                </div>
              )}

              {/* Datalists */}
              <datalist id="tpl-services">{catalog.services.map((s) => <option key={s} value={s} />)}</datalist>
              <datalist id="tpl-plates">{catalog.plates.map((p) => <option key={p} value={p} />)}</datalist>
              <datalist id="tpl-personnel">{catalog.personnel.map((p) => <option key={p} value={p} />)}</datalist>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
